<template>
    <div>
        <h1>API</h1>
        <p>
            All endpoint should return relevant HTTP-status, e.g. <i>200 OK</i> OK, <i>404 Not Found</i> or <i>403 Bad Request</i> etc.
        </p>
        <p>
            In all cases, the request payload (if such exist) and response body (if such exist) should be in <a href="http://www.json.org/" target="_blank">JSON format</a>.
        </p>

        <br /><dm-divider></dm-divider>

        <h2>API Models</h2>
        <p>
            Your serverless kitchen contains two different kinds of items. <i>Recipes</i> and <i>Ingredients</i>
        </p>
        <h3>Recipe</h3>
        <CodeBox><pre>{
    "id": integer, // &gt; 0
    "name": string, // name of the recipe
    "instructions": string, // instructions &amp; howto
    "ingredients": [] // list of ingredients
}</pre></CodeBox>

        <h3>Ingredient</h3>
        <CodeBox><pre>{
    "name": string, // name, e.g. "Sugar"
    "quantity": integer // how much of this ingredient? Must be &gt; 0
}</pre></CodeBox>


        <h3>Example recipe</h3>
        <CodeBox><pre>{
    "id": 1,
    "name": "Sugar cake",
    "instructions": "Mix, then bake in oven",
    "ingredients": [
        { "name": "Sugar", "quantity": 4 },
        { "name": "Cake mix", "quantity": 1 }
    ]
}</pre></CodeBox>

    <br /><dm-divider></dm-divider>
    <h2>API Endpoints</h2>


    <ApiCommand 
        method="GET"
        path="/ping"
        info="Dummy method for testing that the REST controller is set up correcly."
    >
        <pre slot="response">pong</pre>
    </ApiCommand>

    <ApiCommand
        method="POST"
        path="/clear"
        info="Calling this method should clear all entries in the database. No inventory nor recipes should be left. It is mainly used to reset the state when testing. It is typically not a good idea to have this kind of functionality in a real API but part of this exercise for testability and practicing DB commands."
    >
    </ApiCommand>

    <ApiCommand
        method="GET"
        path="/recipes"
        info="Should return a JSON-formatted list of all current recipes. See the model structure of a Recipe above. Please note that the response below is simply an example. If no recipes exist, then an empty list, i.e. [], should be returned."
    >
        <pre slot="response">[
    {
        "id": 1,
        "name": "Sugar cake",
        "instructions": "Mix, then bake in oven",
        "ingredients": [
            { "name": "Sugar", "quantity": 4 },
            { "name": "Cake mix", "quantity": 1 }
        ]
    },
    {
        "id": 2,
        "name": "Cinnamon bun",
        "instructions": "Put cinnamon on the bun",
        "ingredients": [
            { "name": "Cinnamon", "quantity": 1 },
            { "name": "Bun", "quantity": 1 }
        ]
    }
]</pre>
    </ApiCommand>


        <ApiCommand
        method="POST"
        path="/recipes/create"
        info="This is how new recipes are created. Should return the newly created recipe, with its newly calculated id property being set.  By the way, one cannot have Recipes with negative quantities, but who would ever do that?!"
    >
        <pre slot="request">
{
    "name": "Chocolate Doughnuts",
    "instructions": "Melt chocolate, dip the Doughnut",
    "ingredients": [
        { "name": "Chocolate", "quantity": 4 },
        { "name": "Doughnut", "quantity": 4 }
    ]
}
</pre>
        <pre slot="response">{
    "id": 3,
    "name": "Chocolate Doughnuts",
    "instructions": "Melt chocolate, dip the Doughnut",
    "ingredients": [
        { "name": "Chocolate", "quantity": 4 },
        { "name": "Doughnut", "quantity": 4 }
    ]
}</pre>
    </ApiCommand>


    <ApiCommand
        method="GET"
        path="/recipes/{id}"
        info="Get a specific recipe, having id {id}. Should return status: 404 Not Found if there is no such recipe."
    >
        <pre slot="response">{
    "id": 3,
    "name": "Chocolate Doughnuts",
    "instructions": "Melt chocolate, dip the Doughnut",
    "ingredients": [
        { "name": "Chocolate", "quantity": 4 },
        { "name": "Doughnut", "quantity": 4 }
    ]
}</pre>
    </ApiCommand>



    <ApiCommand
        method="DELETE"
        path="/recipes/{id}"
        info="Delete a specific recipe. This method shoud be idempotent."
    >
    </ApiCommand>


    <ApiCommand
        method="PATCH"
        path="/recipes/{id}"
        info="Modify one or more properties of a Recipe. This method shoud be idempotent. Should return 200 OK on success, along with the entire (partly updated) Recipe, or 404 Not Found if no Recipe with id {id} can be found. Please note that it shall not be allowed to change the id-property of a recipe. Trying to do so should result in a 403 Bad Request with some kind of explanation of what went wrong."
    >

        <pre slot="request">{
    "name": "Chocolate Doughnuts v2"
}</pre>

        <pre slot="response">{
    "id": 3,
    "name": "Chocolate Doughnuts v2",
    "instructions": "Melt chocolate, dip the Doughnut",
    "ingredients": [
        { "name": "Chocolate", "quantity": 4 },
        { "name": "Doughnut", "quantity": 4 }
    ]
}</pre>
    </ApiCommand>
    

    <ApiCommand 
        method="POST"
        path="/recipes/{id}/make"
        info="What you all have been waiting for. Everything that is being made will be Yummy! Trying to make something that requires more ingredents than what is currently in the inventory should result in 403 Bad Request with some kind of explanation of what went wrong."
    >
        <pre slot="response">Yummy!</pre>
    </ApiCommand>



    <ApiCommand
        method="GET"
        path="/inventory"
        info="Get a list of the current inventory. Nothing in the inventory? Then [] should be returned. Please note that you should not return entites that have 0 (zero) quantity. That is sooo pointless."
    >
        <pre slot="response">[
    { "name": "Chocolate", "quantity": 4 },
    { "name": "Doughnut", "quantity": 4 },
    { "name": "Sugar", "quantity": 30 },
    { "name": "Cake mix", "quantity": 20 }
]</pre>
    </ApiCommand>

    <ApiCommand
        method="POST"
        path="/inventory/fill"
        info="When you have made your shopping, this is how you add stuff to your kitchen inventory. If you did not have any of that ingredient before, the quantity in the inventory should now equal the quantity specified in the request. If you already had some, then the new updated quantity should be the sum of what you had before plus what was added by this request. For example, given the api calls in these examples (the one above, and this one), you should now have 130 sugar. By the way, one cannot fill the inventory with negative quantities, but who would ever do that?! If it happens, reject the entire request."
    >
        <pre slot="request">[
    { "name": "Sugar", "quantity": 100 },
    { "name": "Cake mix", "quantity": 50 }
]</pre>
    </ApiCommand>


    <h1>Advanced stuff!</h1>
    <p>Let's step the API up a notch. Given the following /recipes</p>
            <CodeBox><pre>[
    {
        "id": 1,
        "name": "Sugar Cake",
        "instructions": "Mix, then bake in oven",
        "ingredients": [
            { "name": "Sugar", "quantity": 4 },
            { "name": "Cake mix", "quantity": 1 }
        ]
    },
    {
        "id": 2,
        "name": "Cinnamon Bun",
        "instructions": "Put cinnamon on the bun",
        "ingredients": [
            { "name": "Cinnamon", "quantity": 1 },
            { "name": "Bun", "quantity": 1 }
        ]
    },
    {
        "id": 3,
        "name": "Cotton Candy",
        "instructions": "Spinn it well!",
        "ingredients": [
            { "name": "Sugar", "quantity": 5 },
            { "name": "Pink color", "quantity": 10 }
        ]
    }
]</pre></CodeBox>


    <p>And, given the following /inventory</p>
            <CodeBox><pre>[
    { "name": "Sugar", "quantity": 44 },
    { "name": "Cake mix", "quantity": 11 },
    { "name": "Cinnamon", "quantity": 5 },
    { "name": "Bun", "quantity": 4 },
    { "name": "Pink color", "quantity": 10 }
]</pre></CodeBox>

    <ApiCommand
        method="GET"
        path="/recipes/get-count-by-recipe"
        info="For each of the current recipes, state how many of that recipe you can make while taking the current inventory into consideration. The calculation is PER RECIPE, ignoring the other recipes when doing the calculation for each recipe. That is, the entire inventory will be used for this single recipe."
    >
        <pre slot="response">[
    {"id":1, "count":11},
    {"id":2, "count":4},
    {"id":3, "count":1}
]
// Total of 16 recipes made, though not possible to do all at the same time. This comment is NOT part of the output</pre>
    </ApiCommand>

    <ApiCommand
        method="GET"
        path="/recipes/optimize-total-count"
        info="What matters is quantity, right? Now, instead of calculating per recipe in isolation, optimize the total count of recipes that you can do. In the example below, the total count is FIFTEEN; 11 of recipe #1 and 4 of recipe #2. Recipe #3 (from the request above) is excluded, as the other recipes consumed its ingredients and this was the optimal solution to maximize the total count of recipes made. Unused inventory is 10 Pink color, and 1 Cinnamon = 11"
    >
        <pre slot="response">{
    "recipes":[
        {"id":1, "count":11},
        {"id":2, "count":4}
    ],
    "recipeCount":15,
    "unusedInventoryCount":11
}</pre>
    </ApiCommand>

    <ApiCommand
        method="GET"
        path="/recipes/optimize-total-waste"
        info="No, wait a minute! Let's minimize the waste instead. Given the total inventory, which recipe(s) should you pick (and how many of them) to reduce the inventory size to as close to zero as possible? That is, when you have done these recipes, there should be as little unused ingredients as possible left in the inventory. The total count will be equal or less than /optimize-total-count, but the sum of all (remaining) ingredients in the inventory should also be equal or less. In this case, 3 Sugar + 2 Cake Mix + 1 Cinnamon = 6 ingredients are unused/wasted (compared to 11 above)."
    >
        <pre slot="response">{
    "recipes":[
        {"id":1, "count":9},
        {"id":2, "count":4},
        {"id":3, "count":1}
    ],
    "recipeCount":14,
    "unusedInventoryCount":6
}</pre>
    </ApiCommand>

    </div>
</template>



<script>
import CodeBox from '@/components/CodeBox.vue'
import ApiCommand from '@/components/ApiCommand.vue'

export default {
    name: 'api',
    components: {
        CodeBox,
        ApiCommand
    }
}

</script>

<style scoped>
.comment {
    color: #666666;
}
</style>
